import { format, formatDistanceToNow } from 'date-fns';
import { stringify } from 'query-string';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAdminOAuthTokens } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label, TextInput } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import Tooltip from '@dashboard/components/Tooltip';
import { Badge } from '@dashboard/components/ui/badge';

type OAuthTokenRow = {
  _id: string;
  clientId: string;
  client?: { name?: string } | null;
  scopes?: string[];
  isRevoked: boolean;
  accessTokenExpiresAt: string;
  lastUsedAt?: string | null;
  usageCount?: number;
  createdAt: string;
  seller?: { _id: string; displayName?: string; name?: string } | null;
  user?: { _id: string; email?: string; name?: string } | null;
};

type StatusFilter = 'all' | 'active' | 'revoked';

// The URL carries status as a word, not isRevoked=true/false, because useUrlQuery
// parses booleans and a reloaded filter would no longer match the select's values.
const IS_REVOKED_BY_STATUS: Record<StatusFilter, boolean | undefined> = {
  all: undefined,
  active: false,
  revoked: true,
};

const STATUS_BADGES = {
  active: { label: 'Active', variant: 'success' },
  revoked: { label: 'Revoked', variant: 'destructive' },
  expired: { label: 'Expired', variant: 'warning' },
} as const;

function getTokenStatus(token: OAuthTokenRow) {
  if (token.isRevoked) return 'revoked';
  if (new Date(token.accessTokenExpiresAt) <= new Date()) return 'expired';
  return 'active';
}

const formatDate = (value: string) => format(new Date(value), 'dd MMM yyyy HH:mm');

const OAuthTokens = () => {
  const history = useHistory();
  const query = useUrlQuery();

  const sortBy = typeof query.sortBy === 'string' ? query.sortBy : 'createdAt';
  const sortDirection = Number(query.sortDirection) === 1 ? 1 : -1;
  const status: StatusFilter =
    query.status === 'active' || query.status === 'revoked' ? query.status : 'all';
  const { search, searchInput, setSearchInput } = useSearchInput(
    query.search == null ? '' : String(query.search),
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useAdminOAuthTokens({
    sortBy,
    sortDirection,
    search,
    isRevoked: IS_REVOKED_BY_STATUS[status],
    limit: 20,
  });

  const sorting = useMemo(
    () => [{ id: sortBy, desc: sortDirection === -1 }],
    [sortBy, sortDirection],
  );

  const flatData: OAuthTokenRow[] = useMemo(
    () => data?.pages?.flatMap((page: any) => page.data) ?? [],
    [data],
  );

  const total: number | undefined = data?.pages[0]?.total;

  const replaceUrl = (
    changes: Partial<{
      sortBy: string;
      sortDirection: number;
      status: StatusFilter;
      search: string;
    }>,
  ) => {
    const next = { sortBy, sortDirection, status, search: searchInput.trim(), ...changes };
    history.replace({
      search: stringify({
        sortBy: next.sortBy,
        sortDirection: next.sortDirection,
        ...(next.status !== 'all' && { status: next.status }),
        ...(next.search && { search: next.search }),
      }),
    });
  };

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      replaceUrl({ sortBy: id, sortDirection: desc ? -1 : 1 });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    replaceUrl({ search: value.trim() });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'seller',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Store" />,
        cell: ({ row }: any) => {
          const { seller } = row.original as OAuthTokenRow;
          if (!seller) return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          return (
            <Link to={`/admin/merchants/${seller._id}`}>{seller.displayName || seller.name}</Link>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'user',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="User" />,
        cell: ({ row }: any) =>
          row.original.user?.email || (
            <span className="text-zinc-400 dark:text-muted-foreground">—</span>
          ),
        enableSorting: false,
      },
      {
        accessorKey: 'clientId',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Client" />,
        cell: ({ row }: any) => row.original.client?.name || row.original.clientId,
        enableSorting: true,
      },
      {
        accessorKey: 'scopes',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Scopes" />,
        cell: ({ row }: any) => {
          const scopes: string[] = row.original.scopes || [];
          if (scopes.length === 0)
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          return (
            <div className="flex gap-1">
              {scopes.map(scope => (
                <Badge key={scope} variant="outline">
                  {scope}
                </Badge>
              ))}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'isRevoked',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }: any) => {
          const { label, variant } = STATUS_BADGES[getTokenStatus(row.original)];
          return <Badge variant={variant}>{label}</Badge>;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'usageCount',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Uses" />,
        cell: ({ row }: any) => {
          const count: number = row.original.usageCount || 0;
          return (
            <span className={count === 0 ? 'text-zinc-400 dark:text-muted-foreground' : ''}>
              {count === 0 ? '—' : count.toLocaleString()}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'lastUsedAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Last used" />,
        cell: ({ row }: any) => {
          const { lastUsedAt } = row.original as OAuthTokenRow;
          if (!lastUsedAt)
            return <span className="text-zinc-400 dark:text-muted-foreground">—</span>;
          return (
            <Tooltip label={formatDate(lastUsedAt)} side="bottom">
              <span className="cursor-help">
                {formatDistanceToNow(new Date(lastUsedAt), { addSuffix: true })}
              </span>
            </Tooltip>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Issued" />,
        cell: ({ row }: any) => formatDate(row.original.createdAt),
        enableSorting: true,
      },
      {
        accessorKey: 'accessTokenExpiresAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Expires" />,
        cell: ({ row }: any) => formatDate(row.original.accessTokenExpiresAt),
        enableSorting: true,
      },
    ],
    [],
  );

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto flex h-9 items-center text-sm text-gray-500 dark:text-muted-foreground">
        {total !== undefined && `${total} ${total === 1 ? 'token' : 'tokens'}`}
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select
          name="status"
          value={status}
          onValueChange={value => replaceUrl({ status: value as StatusFilter })}
        >
          <SelectTrigger id="status" className="!w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="revoked">Revoked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              id="search"
              name="search"
              onChange={(e: any) => handleSearchChange(e.target.value)}
              placeholder="Search by store, email or client"
              value={searchInput}
            />
            {searchInput && (
              <button
                aria-label="Clear search"
                onClick={() => handleSearchChange('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>OAuth tokens</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No OAuth tokens found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default OAuthTokens;
